import { contentTypes, stepTypes } from '../step/step-constants';
import { stepModel } from '../step/step-model';
import { readAloudModel } from './readaloud-model';

/**
 * Pronunciation doesn't work well with htmlText so we are stripping raw text to show.
 */
function parsePronunciationHtmlTextToPureText(htmlText) {
  var el = document.createElement('span');
  el.innerHTML = htmlText;
  return el.innerText || '';
}

function isPronunciation(step) {
  if (step.type !== stepTypes.PHONEME) {
    return false;
  }
  var xmlAlignmentData = readAloudModel.getXmlAlignmentData(step);
  return !xmlAlignmentData;
}

function getCountOfPronunciationItems(step) {
  var pronunciationItemCount = 0;
  if (isPronunciation(step)) {
    if (isMultiplePronunciation(step)) {
      var pronunciationItems = getPronunciationMultipleItems(step);
      pronunciationItemCount = pronunciationItems.length;
    } else {
      pronunciationItemCount = 1;
    }
  }
  return pronunciationItemCount;
}

function isMultiplePronunciation(step) {
  // there exists data where the step has only one substep, in the carousel.
  //  per CD-606 we should treat that as a single step, not a substep
  var isSingleSubstep = !!step.content[0] && Array.isArray(step.content[0].carousel) && step.content[0].carousel.length === 1;
  // sometimes the carousel item is an array which could at least in theory have more than one item in it so test that
  if (isSingleSubstep && Array.isArray(step.content[0].carousel[0])) {
    isSingleSubstep = step.content[0].carousel[0].length === 1;
  }
  if (isSingleSubstep) {
    return false;
  }
  // here are the three ways the pronunciation items might be packaged:
  //  content as an array, content[0] as an array, or content[0].carousel as an array
  return step.content.length > 1 || step.content[0].length > 1 || step.content[0].carousel;
}

function parsePronunciationData(step) {
  if (isMultiplePronunciation(step)) {
    return parsePronunciationMultiple(step);
  }
  return parsePronunciationWordSentence(step);
}

function parsePronunciationWordSentence(step) {
  var pronunciationItem = void 0;
  if (step.content[0].carousel) {
    // if we got here there must be a single item in the carousel
    //  and if the item is an array it must only have a single member
    pronunciationItem = step.content[0].carousel[0];
    if (Array.isArray(pronunciationItem)) {
      pronunciationItem = pronunciationItem[0];
    }
    // make this weird data - which has an empty 'correct' array -
    //  look like "normal" single-item content, but don't do it twice
    if (step.correct.length === 0) {
      step.correct.push(pronunciationItem.id);
    }
  } else {
    pronunciationItem = step.content[0][0];
  }
  var audios = void 0;
  var text = void 0;
  if (pronunciationItem) {
    if (pronunciationItem.audios && Array.isArray(pronunciationItem.audios) && pronunciationItem.audios.length > 0) {
      audios = pronunciationItem.audios[0];
    }
    if (pronunciationItem.text) {
      text = pronunciationItem.text;
    } else if (pronunciationItem.htmlText) {
      text = parsePronunciationHtmlTextToPureText(pronunciationItem.htmlText);
    }
  }
  return {
    id: pronunciationItem.id,
    sreText: pronunciationItem.sreText,
    images: pronunciationItem.images,
    audios: audios,
    text: text
  };
}

function getPronunciationMultipleItems(step) {
  // the pronunciation steps are richly diverse in the way they arrive to the client
  //  sometimes - ideally - there is a carousel of items
  //  sometimes the content array will have the items all separately
  //  sometimes the content array will only have one item, but it will be an array of the items
  var pronunciationItems = void 0;
  if (step.content[0].carousel) {
    pronunciationItems = step.content[0].carousel;
  } else if (step.content.length > 1) {
    pronunciationItems = step.content;
  } else if (Array.isArray(step.content[0]) && step.content[0].length > 1) {
    pronunciationItems = step.content[0];
  }
  if (pronunciationItems) {
    var flatItems = [];
    pronunciationItems.forEach(function (item) {
      if (Array.isArray(item) && item[0]) {
        flatItems.push(item[0]);
      } else {
        flatItems.push(item);
      }
    });
    pronunciationItems = flatItems;
  }
  return pronunciationItems;
}

function parsePronunciationMultiple(step) {
  var pronunciationItems = getPronunciationMultipleItems(step);
  var keyConcepts = void 0;
  if (Array.isArray(step.keyConcepts) && step.keyConcepts.length > 0) {
    keyConcepts = step.keyConcepts[0];
  }
  var ipakey = getTitleText(keyConcepts);
  var pronunciationRule = getPronunciationRule(keyConcepts);
  var videos = keyConcepts && stepModel.parseContentByType(contentTypes.VIDEOS, keyConcepts.pronunciationRule);
  var data = {
    texts: pronunciationItems,
    ipakey: ipakey,
    pronunciationRule: pronunciationRule,
    videos: videos
  };
  return data;
}

//TODO: localization with languages
function getTitleText(data, languages) {
  if (data && data.title && data.title.localizations && Array.isArray(data.title.localizations)) {
    var enTitles = data.title.localizations.filter(function (localizedTitle) {
      return localizedTitle.locale === 'en-US';
    });
    if (enTitles.length > 0) {
      return enTitles[0].text;
    }
  }
}

//TODO: localization with languages
function getPronunciationRule(data, languages) {
  if (data && data.pronunciationRule && data.pronunciationRule.localizations && Array.isArray(data.pronunciationRule.localizations)) {
    var enRules = data.pronunciationRule.localizations.filter(function (localizedRule) {
      return localizedRule.locale === 'en-US';
    });
    if (enRules.length > 0) {
      return enRules[0].text;
    }
  }
}

// TODO: is PronunciationPhoneme handled correctly here?
// handles ReadAloud, PronunciationWord, PronunciationSentence
// ReadAloud answer will contain 'user_words' if the step is incorrect.
var getAnswers = function getAnswers(step, values) {
  var correctId = step.correct[0];
  var submitted = values[0];
  var isCorrect = correctId === submitted;
  return [{
    answer: submitted,
    correct: isCorrect
  }];
};

var isCorrect = function isCorrect(answers) {
  return !!answers.find(function (a) {
    return a.correct;
  });
};

export var pronunciationModel = {
  isPronunciation: isPronunciation,
  isMultiplePronunciation: isMultiplePronunciation,
  getCountOfPronunciationItems: getCountOfPronunciationItems,
  getPronunciationMultipleItems: getPronunciationMultipleItems,
  parsePronunciationData: parsePronunciationData,
  getAnswers: getAnswers,
  isCorrect: isCorrect
};